import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG, AppConfig } from '@src/app.config';
import { Observable } from 'rxjs';
import { DocumentDto, ListDirectoryResponse } from './patient-documents-api.service';

export interface RequestNewSecureUploadLinkResponse {
  success: boolean;
  message: string;
  cooldownSeconds: number;
}

@Injectable({ providedIn: 'root' })
export class SecureDocumentUploadApiService {
  private readonly base = `${this.appConfig.angularEndpoint}/secureLink`;

  constructor(
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
  ) {}

  list(token: string, pageSize = 100, pageToken = ''): Observable<ListDirectoryResponse> {
    const url = this.withToken(`${this.base}/secureUploadedDocuments`, token, {
      pageSize: String(pageSize),
      pageToken,
    });
    return this.http.get<ListDirectoryResponse>(url);
  }

  upload(token: string, file: File): Observable<void> {
    const url = this.withToken(`${this.base}/uploadSecureDocument`, token);
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<void>(url, form);
  }

  download(token: string, documentId: string): Observable<Blob> {
    const url = this.withToken(`${this.base}/downloadSecureDocument`, token, {
      documentId,
    });
    return this.http.get(url, {
      responseType: 'blob',
    }) as Observable<Blob>;
  }

  rename(token: string, documentId: string, newFilename: string): Observable<DocumentDto> {
    const url = this.withToken(`${this.base}/renameSecureDocument`, token);
    return this.http.post<DocumentDto>(url, {
      documentId,
      newFilename,
    });
  }

  delete(token: string, documentId: string): Observable<void> {
    const url = this.withToken(`${this.base}/secureDocument/${encodeURIComponent(documentId)}`, token);
    return this.http.delete<void>(url);
  }

  confirm(token: string): Observable<void> {
    const url = this.withToken(`${this.base}/confirmSecureUpload`, token);
    return this.http.post<void>(url, {});
  }

  requestNewUploadLink(token: string): Observable<RequestNewSecureUploadLinkResponse> {
    const url = `${this.base}/requestNewSecureUploadLink`;
    return this.http.post<RequestNewSecureUploadLinkResponse>(url, { token });
  }

  private withToken(url: string, token: string, params: Record<string, string> = {}): string {
    const search = new URLSearchParams({
      token: token ?? '',
      ...params,
    });
    return `${url}?${search.toString()}`;
  }
}
